import { useEffect, useMemo, useState } from 'react';

import { useMovementConcepts } from '../../../hooks/useMovementConcepts';
import {
  useCreateSupplierInvoice,
  useUpdateSupplierInvoice,
  useUploadSupplierInvoiceAttachment,
} from '../../../hooks/useSupplierLedger';
import { invoiceSections } from '../constants/supplierLedgerUiConstants';
import {
  buildAmountLinesFromInvoice,
  buildInvoiceFormFromInvoice,
  calculateAmountLine,
  createAmountLine,
  emptyInvoiceForm,
  hasGeneratedPaymentOrder,
} from '../utils/supplierLedgerHelpers';
import { parseNumber } from '../utils/supplierLedgerFormatters';

const useSupplierInvoiceDialogController = ({
  invoiceToEdit,
  onClose,
  open,
  providers,
}) => {
  const [form, setForm] = useState(emptyInvoiceForm);
  const [amountLines, setAmountLines] = useState(() => [createAmountLine()]);
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState(invoiceSections[0].id);

  const createInvoice = useCreateSupplierInvoice();
  const updateInvoice = useUpdateSupplierInvoice();
  const uploadAttachment = useUploadSupplierInvoiceAttachment();
  const { data: movementConcepts = [], isLoading: movementConceptsLoading } = useMovementConcepts();

  const isEditMode = Boolean(invoiceToEdit?.uuid);
  const currentAttachmentName = invoiceToEdit?.attachments?.[0]?.original_filename || '';
  const locksFinancialFields = isEditMode && (
    hasGeneratedPaymentOrder(invoiceToEdit) || Number(invoiceToEdit?.paid_amount || 0) > 0
  );

  const providerOptions = useMemo(
    () => providers.map((provider) => ({
      value: provider.uuid,
      label: provider.legal_business_name || provider.company || provider.cuit || 'Proveedor sin nombre',
    })),
    [providers],
  );

  const selectedProviderName = useMemo(
    () => providerOptions.find((provider) => provider.value === form.uuid_legal_entity)?.label || invoiceToEdit?.provider_name || '',
    [form.uuid_legal_entity, invoiceToEdit?.provider_name, providerOptions],
  );

  const movementConceptOptions = useMemo(() => {
    const options = movementConcepts
      .map((concept) => ({
        value: concept.name,
        label: concept.name,
        code: concept.code,
        description: concept.description,
      }))
      .sort((left, right) => String(left.label || '').localeCompare(String(right.label || '')));

    if (form.concept && !options.some((option) => option.value === form.concept)) {
      return [
        {
          value: form.concept,
          label: form.concept,
          code: '',
          description: 'Concepto anterior fuera del catálogo activo',
          legacy: true,
        },
        ...options,
      ];
    }

    return options;
  }, [form.concept, movementConcepts]);

  const amountTotals = useMemo(() => {
    const baseTotals = amountLines.reduce(
      (acc, line) => {
        const lineAmounts = calculateAmountLine(line);
        acc.gross += lineAmounts.gross;
        acc.net += lineAmounts.net;
        acc.vat += lineAmounts.vat;
        return acc;
      },
      { gross: 0, net: 0, vat: 0 },
    );
    const perceptions = parseNumber(form.vat_perception_amount) + parseNumber(form.gross_income_perception_amount);

    return {
      ...baseTotals,
      perceptions,
      total: baseTotals.gross + perceptions,
    };
  }, [amountLines, form.gross_income_perception_amount, form.vat_perception_amount]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAmountLineChange = (lineId, field, value) => {
    setAmountLines((prev) => prev.map((line) => (
      line.id === lineId ? { ...line, [field]: value } : line
    )));
  };

  const handleAddAmountLine = () => {
    setAmountLines((prev) => [...prev, createAmountLine()]);
  };

  const handleRemoveAmountLine = (lineId) => {
    setAmountLines((prev) => {
      if (prev.length === 1) return [createAmountLine()];
      return prev.filter((line) => line.id !== lineId);
    });
  };

  useEffect(() => {
    if (!open) return;

    if (invoiceToEdit) {
      setForm(buildInvoiceFormFromInvoice(invoiceToEdit));
      setAmountLines(buildAmountLinesFromInvoice(invoiceToEdit));
    } else {
      setForm(emptyInvoiceForm());
      setAmountLines([createAmountLine()]);
    }
    setFile(null);
    setError('');
    setActiveSection(invoiceSections[0].id);
  }, [invoiceToEdit, open]);

  const handleClose = () => {
    setForm(emptyInvoiceForm());
    setAmountLines([createAmountLine()]);
    setFile(null);
    setError('');
    setActiveSection(invoiceSections[0].id);
    onClose();
  };

  const buildPayload = () => {
    const payload = {
      ...form,
      due_date: form.due_date || null,
      exchange_rate_to_ars: form.currency === 'USD' ? parseNumber(form.exchange_rate_to_ars) : null,
      net_amount: amountTotals.net,
      vat_amount: amountTotals.vat,
      vat_perception_amount: parseNumber(form.vat_perception_amount),
      gross_income_perception_amount: parseNumber(form.gross_income_perception_amount),
      total_amount: amountTotals.total,
      tax_lines: amountLines
        .map((line) => {
          const lineAmounts = calculateAmountLine(line);
          return {
            tax_type: 'IVA',
            rate: lineAmounts.vatRate,
            taxable_base_amount: lineAmounts.net,
            amount: lineAmounts.vat,
          };
        })
        .filter((line) => line.taxable_base_amount > 0 || line.amount > 0),
    };

    if (isEditMode) {
      delete payload.uuid_legal_entity;
      delete payload.duplicate_override_reason;
      delete payload.status;
      return payload;
    }

    return {
      ...payload,
      duplicate_override_reason: form.duplicate_override_reason || null,
      status: 'open',
    };
  };

  const handleSubmit = async () => {
    setError('');
    const selectedConcept = movementConceptOptions.find((option) => option.value === form.concept);
    if (!selectedConcept || selectedConcept.legacy) {
      setError('Debe seleccionar un concepto activo del catálogo de conceptos de movimiento.');
      setActiveSection('accounting');
      return;
    }
    if (amountTotals.total <= 0) {
      setError('Debe cargar al menos un importe mayor a cero.');
      setActiveSection('amounts');
      return;
    }
    try {
      const savedInvoice = isEditMode
        ? await updateInvoice.mutateAsync({ id: invoiceToEdit.uuid, data: buildPayload() })
        : await createInvoice.mutateAsync(buildPayload());
      if (file) {
        await uploadAttachment.mutateAsync({ invoiceId: savedInvoice.uuid, file });
      }
      handleClose();
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message);
    }
  };

  return {
    activeSection,
    amountLines,
    amountTotals,
    currentAttachmentName,
    error,
    file,
    form,
    handleAddAmountLine,
    handleAmountLineChange,
    handleChange,
    handleClose,
    handleRemoveAmountLine,
    handleSubmit,
    isEditMode,
    isSaving: createInvoice.isPending || updateInvoice.isPending || uploadAttachment.isPending,
    locksFinancialFields,
    movementConceptOptions,
    movementConceptsLoading,
    providerOptions,
    selectedProviderName,
    setActiveSection,
    setFile,
  };
};

export default useSupplierInvoiceDialogController;
